import { format } from "date-fns";
import { z } from "zod";
import {
  API_PREFIX,
  HttpError,
  paginatedFromDto,
  type HttpClient,
} from "@nasam/kernel";
import {
  noDeviceCapacityOf,
  type NoDeviceCapacityRefusal,
} from "../../../shared/capacity/overCapacity";
import type {
  DeviceStatusChange,
  DevicesRepository,
  DeviceWrite,
} from "../domain";
import {
  archivedDeviceMatchResponseDtoSchema,
  archivedDevicesListResponseDtoSchema,
  deviceBrandsListResponseDtoSchema,
  deviceCapacityResponseDtoSchema,
  deviceCreateDtoSchema,
  deviceDeleteResponseDtoSchema,
  deviceDetailResponseDtoSchema,
  deviceModelsListResponseDtoSchema,
  deviceResponseDtoSchema,
  devicesListResponseDtoSchema,
  deviceTypesListResponseDtoSchema,
  deviceUpdateDtoSchema,
  deviceWriteResponseDtoSchema,
} from "./deviceDtos";
import {
  archivedDeviceFromDto,
  deviceBrandFromDto,
  deviceCreateToDto,
  deviceDetailFromDto,
  deviceFromDto,
  deviceListItemFromDto,
  deviceModelFromDto,
  deviceTypeFromDto,
  deviceUpdateToDto,
} from "./deviceMappers";

const DEVICES_PATH = `${API_PREFIX}/devices`;

/** The device a write answers with (and its localized message), and where
 *  the devices now stand. */
function deviceWriteOf<S extends string>(
  status: S,
  response: unknown,
): DeviceWrite<S> {
  const parsed = deviceWriteResponseDtoSchema.parse(response);
  return {
    status,
    device: {
      ...deviceFromDto(parsed.data),
      ...(parsed.message !== undefined
        ? { responseMessage: parsed.message }
        : {}),
    },
    usage: parsed.data.usageInfo,
  };
}

/** The write, or the refusal it gets with no devices subscription. */
/**
 * The term's last day as the calendar day picked, whatever the browser's
 * time zone: the API reads it as a day in the subscription's (Riyadh), so
 * midnight UTC of that date (its own example) lands on it.
 */
export function endDateForApi(day: Date): string {
  return `${format(day, "yyyy-MM-dd")}T00:00:00.000Z`;
}

function statusBodyOf(change: DeviceStatusChange) {
  return change.status === "ACTIVE"
    ? { status: change.status, endDate: endDateForApi(change.endDate) }
    : { status: change.status };
}

const errorCodeDtoSchema = z.object({ code: z.string() });

function errorCodeOf(error: HttpError): string | undefined {
  const parsed = errorCodeDtoSchema.safeParse(error.data);
  return parsed.success ? parsed.data.code : undefined;
}

async function unlessNoCapacity<T>(
  write: () => Promise<T>,
): Promise<T | NoDeviceCapacityRefusal> {
  try {
    return await write();
  } catch (error) {
    const refusal = noDeviceCapacityOf(error);
    if (refusal !== undefined) return refusal;
    throw error;
  }
}

export function createDevicesHttpRepository(
  http: HttpClient,
): DevicesRepository {
  return {
    async list(query) {
      const response = await http.get(DEVICES_PATH, { params: query });
      return paginatedFromDto(
        devicesListResponseDtoSchema.parse(response),
        deviceListItemFromDto,
      );
    },
    async listArchived(query) {
      const response = await http.get(`${DEVICES_PATH}/archived`, {
        params: query,
      });
      return paginatedFromDto(
        archivedDevicesListResponseDtoSchema.parse(response),
        archivedDeviceFromDto,
      );
    },
    async getById(id) {
      const response = await http.get(`${DEVICES_PATH}/${id}`);
      return deviceDetailFromDto(
        deviceDetailResponseDtoSchema.parse(response).data,
      );
    },
    async update(id, input) {
      const response = await http.patch(
        `${DEVICES_PATH}/${id}`,
        deviceUpdateDtoSchema.parse(deviceUpdateToDto(input)),
      );
      const parsed = deviceResponseDtoSchema.parse(response);
      const device = deviceFromDto(parsed.data);
      return {
        ...device,
        ...(parsed.message !== undefined
          ? { responseMessage: parsed.message }
          : {}),
      };
    },
    async listTypes(query) {
      const response = await http.get(`${DEVICES_PATH}/types`, {
        params: query,
      });
      return paginatedFromDto(
        deviceTypesListResponseDtoSchema.parse(response),
        deviceTypeFromDto,
      );
    },
    async listBrands(query) {
      const response = await http.get(`${DEVICES_PATH}/brands`, {
        params: query,
      });
      return paginatedFromDto(
        deviceBrandsListResponseDtoSchema.parse(response),
        deviceBrandFromDto,
      );
    },
    async listModels(query) {
      const response = await http.get(`${DEVICES_PATH}/models`, {
        params: query,
      });
      return paginatedFromDto(
        deviceModelsListResponseDtoSchema.parse(response),
        deviceModelFromDto,
      );
    },
    async create(input) {
      try {
        const response = await http.post(
          DEVICES_PATH,
          deviceCreateDtoSchema.parse(deviceCreateToDto(input)),
        );
        return deviceWriteOf("created", response);
      } catch (error) {
        const refusal = noDeviceCapacityOf(error);
        if (refusal !== undefined) return refusal;
        // Used to answer 201 with the trimmed summary below; now 409s
        // instead (code "ARCHIVED_DEVICE_FOUND"), same trimmed shape under
        // `data` -- still not an error from the form's own perspective,
        // just an offer to restore (see CreateDeviceResult). Disambiguated
        // by shape, not the code string, same as every other archived-match
        // endpoint in this app.
        const archived =
          error instanceof HttpError && error.status === 409
            ? archivedDeviceMatchResponseDtoSchema.safeParse(error.data)
            : undefined;
        if (archived === undefined || !archived.success) throw error;
        return {
          status: "archived-match",
          match: archived.data.data,
          message: archived.data.message,
        };
      }
    },
    async delete(id) {
      const response = await http.delete(`${DEVICES_PATH}/${id}`);
      const { message } = deviceDeleteResponseDtoSchema.parse(response);
      return { responseMessage: message };
    },
    async restore(id) {
      return unlessNoCapacity(async () =>
        deviceWriteOf(
          "restored",
          await http.patch(`${DEVICES_PATH}/${id}/restore`),
        ),
      );
    },
    async getCapacity() {
      const { data } = deviceCapacityResponseDtoSchema.parse(
        await http.get(`${DEVICES_PATH}/capacity`),
      );
      return {
        paidQuantity: data.quantity,
        used: data.used,
        remaining: data.remaining,
      };
    },
    // The refusals the activation story shows its own way: no devices
    // subscription for the term, the status changed meanwhile (any other
    // 409), the end date no longer after today (400). A server or network
    // failure gets the story's own message, not the raw one; other 4xx
    // (permission, not found) keep the server's.
    async setStatus(id, change) {
      try {
        return await unlessNoCapacity(async () =>
          deviceWriteOf(
            "updated",
            await http.patch(
              `${DEVICES_PATH}/${id}/status`,
              statusBodyOf(change),
            ),
          ),
        );
      } catch (error) {
        if (!(error instanceof HttpError)) throw error;
        if (error.status === 409) {
          return errorCodeOf(error) === "DEVICE_SUBSCRIPTION_REQUIRED"
            ? { status: "subscription-required" }
            : { status: "status-changed" };
        }
        if (error.status === 400 && change.status === "ACTIVE") {
          return { status: "invalid-end-date", message: error.message };
        }
        if (error.status === undefined || error.status >= 500) {
          return { status: "failed" };
        }
        throw error;
      }
    },
  };
}
